import { useState, useEffect, useMemo, useRef, useId } from 'react'
import { FolderTree, Globe } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useBuilderPages } from './hooks/useBuilderPages'
import { BuilderPageAutocomplete } from './BuilderPageAutocomplete'
import { useScrimDismiss } from '../shared/hooks/useScrimDismiss'
import { useEscapeClose } from '../shared/hooks/useEscapeClose'

/**
 * Créer une page VIERGE, ou un MODÈLE — vierge lui aussi, ou DÉMARRÉ DEPUIS UNE
 * PAGE DU SITE.
 *
 * PAGE VIERGE : quand le site a un MODÈLE PAR DÉFAUT (`default_model`, réglé
 * dans la modale de `/sites`), la page créée en reprend les blocs — c'est le
 * point même d'avoir désigné un modèle par défaut : que l'habillage du site
 * (header, footer…) arrive sans un second geste. La case à cocher le dit et
 * le laisse désactiver : jamais totalement invisible, toujours réversible en
 * un clic pour qui veut réellement partir de rien.
 *
 * MODÈLE : « Partir de » offre en plus de choisir n'importe quelle page du
 * site — le chemin naturel vers le PREMIER modèle d'un site, dont
 * l'habillage existe déjà sur une page réelle. Repartir d'elle évite de le
 * reconstruire à l'identique — et c'est la MÊME opération que « créer une
 * page à partir d'un modèle », dans l'autre sens
 * (`/api/builder/page/duplicate` avec `asTemplate`).
 *
 * Dans les deux cas, la duplication COPIE les blocs. Pour qu'un header reste
 * ensuite commun à toutes les pages qui en descendent, il faut y remplacer la
 * copie par un BLOC LIÉ (« Ajouter un bloc existant ») — d'où la note sous le
 * champ « Partir de ».
 *
 * `siteId` (optionnel) : le gid du site auquel rattacher la page — c'est ce que
 * passe le bouton « Créer une page » de l'écran d'un site (`/builder?create=blank&
 * site=<gid>`). Le rattachement voyage dans le MÊME save que la création (cf.
 * useBuilderPages.createPage) ; la modale se contente de l'annoncer, en allant
 * chercher le NOM du site pour que l'utilisateur voie où il écrit.
 *
 * `directoryId` (optionnel) : le tid du répertoire du WIKI où ranger la page —
 * ce que passe l'écran d'un espace documentaire. Il exclut `siteId` : une page
 * rangée n'appartient à aucun site. La création emprunte alors l'API decoupled
 * (`?directory=<tid>`, cf. useBuilderPages.createInDirectory) et l'écran ouvert
 * ensuite est `/docs/page/<nid>` — pas `/builder/<slug>`. « Partir de » ne
 * s'affiche pas : côté wiki, un modèle part d'un modèle, sur aperçu
 * (BuilderTemplatePicker).
 *
 * `asTemplate` : le geste « créer un modèle », depuis l'écran d'un site.
 *
 * `defaultModelNid` : le modèle par défaut du site (entier, calculé côté back —
 * cf. `defaultModelNid` de `/decoupled-api/group/site/{gid}`). Résolu ici en
 * SLUG via `templates` (le nid seul ne suffit pas à dupliquer).
 */
export const BuilderCreateModal = ({ onClose, siteId = null, directoryId = null, asTemplate = false, defaultModelNid = null }) => {
  const scrim = useScrimDismiss(onClose)
  // `templates` sert à résoudre `defaultModelNid` en slug ; `pages` n'est plus
  // chargé ici — le sélecteur « Partir de » fait sa propre recherche bornée
  // (cf. BuilderPageAutocomplete), un site pouvant porter des centaines de pages.
  const { createPage, duplicatePage, createInDirectory, templates } = useBuilderPages(siteId, directoryId)
  const navigate = useNavigate()
  const titleId = useId()
  const fieldId = useId()
  const sourceId = useId()
  const defaultModelId = useId()

  // Slug de la page dont part un MODÈLE, "" pour vierge. N'a de sens que pour
  // `asTemplate` : dupliquer une page pour en faire une autre page est déjà le
  // bouton « Dupliquer » de la liste.
  const [sourceSlug, setSourceSlug] = useState('')
  const [title, setTitle] = useState('')
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState(null)
  const [siteName, setSiteName] = useState('')
  const inputRef = useRef(null)

  // Le modèle par défaut du site, résolu en objet {slug, title}. `undefined`
  // tant que `templates` n'est pas encore chargé — traité comme « aucun » : le
  // temps d'un aller-retour, la page vierge reste vierge plutôt que d'afficher
  // puis de faire disparaître une case à cocher.
  const defaultModel = useMemo(
    () => (defaultModelNid ? templates.find(t => t.nid === defaultModelNid) ?? null : null),
    [templates, defaultModelNid],
  )
  // Cochée par défaut DÈS que le modèle par défaut est là — c'est la raison
  // d'être du réglage. Un utilisateur qui veut réellement une page nue la
  // décoche ; ce choix n'a plus à être refait entre-temps.
  const [useDefaultModel, setUseDefaultModel] = useState(true)

  // Focus automatique sur le champ titre à l'ouverture
  useEffect(() => { inputRef.current?.focus() }, [])

  // Nom de la CIBLE (site ou répertoire du wiki), via l'API générique — donc
  // déjà filtrée par les droits. Enveloppe Syncable pour qui peut l'écrire,
  // objet plat sinon : les deux formes cohabitent selon le lecteur (même piège
  // que BuilderPageAddressPanel).
  useEffect(() => {
    const url = siteId
      ? `/decoupled-api/group/site/${siteId}?display=decoupled`
      : (directoryId ? `/decoupled-api/taxonomy_term/doc_directory/${directoryId}?display=decoupled` : null)
    if (!url) return
    let cancelled = false
    fetch(url, { credentials: 'include' })
      .then(res => (res.ok ? res.json() : null))
      .then(payload => {
        if (cancelled || !payload) return
        const target = payload.data ?? payload
        if (target?.name) setSiteName(target.name)
      })
      .catch(() => { /* le nom est un confort : son absence ne bloque rien */ })
    return () => { cancelled = true }
  }, [siteId, directoryId])

  useEscapeClose(onClose)

  const handleSubmit = async (e) => {
    e.preventDefault()
    const trimmed = title.trim()
    if (!trimmed) return

    setCreating(true)
    setError(null)
    try {
      // WIKI : la page naît DANS un répertoire, et se lit à `/docs/page/<nid>`.
      // Pas de « partir de » ici — le geste « à partir d'un modèle » a son
      // propre écran (BuilderTemplatePicker), sur aperçu.
      if (directoryId) {
        const nid = await createInDirectory(trimmed, null, asTemplate)
        navigate(`/docs/page/${nid}?edit=1`)
        return
      }

      // Un seul point de décision pour les deux flux : la page vierge part du
      // modèle par défaut (si coché), le modèle part de la page choisie dans
      // « Partir de ». Vide dans les deux cas ⇒ vraiment rien à copier.
      const startSlug = asTemplate
        ? sourceSlug
        : (defaultModel && useDefaultModel ? defaultModel.slug : '')

      // Deux chemins pour un même résultat : une page neuve, ou la copie d'une
      // page existante. Le second passe par la duplication, qui clone les unités
      // avec des `builderId` frais — jamais par un save qui réutiliserait les
      // nœuds de la source.
      const slug = startSlug
        ? await duplicatePage(startSlug, trimmed, siteId, asTemplate)
        : await createPage(trimmed, [], siteId, asTemplate)
      // Pas de `onClose()` ici : la navigation démonte l'écran qui porte la
      // modale. `onClose` ne signifie donc que « l'utilisateur a renoncé » —
      // c'est ce qui permet à l'appelant de le ramener d'où il vient.
      navigate(`/builder/${slug}?edit=1`)
    } catch (err) {
      // Le message porte l'information utile (URI déjà prise, réservée…) :
      // le montrer plutôt qu'un « une erreur est survenue » indépassable.
      setError(err?.message || 'Une erreur est survenue. Réessayez.')
      console.error(err)
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="modal-scrim" {...scrim}>
      <form
        className="modal-panel w-full max-w-md"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={e => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="modal-header">
          <h2 id={titleId}>Créer {asTemplate ? 'un gabarit' : 'une page vierge'}</h2>
        </div>

        <div className="modal-body space-y-4">
          {(siteId || directoryId) && (
            <p className="flex items-center gap-2 rounded-lg border border-iris-100 bg-iris-50 px-3 py-2 text-xs text-iris-700">
              {siteId
                ? <Globe size={14} className="shrink-0" aria-hidden="true" />
                : <FolderTree size={14} className="shrink-0" aria-hidden="true" />}
              <span>
                {asTemplate ? 'Ce gabarit sera rattaché' : 'Cette page sera rattachée'}
                {siteId ? ' au site ' : ' au répertoire '}
                <strong>{siteName || `#${siteId || directoryId}`}</strong>.
              </span>
            </p>
          )}

          <div>
            <label htmlFor={fieldId} className="field-label">Titre {asTemplate ? 'du gabarit' : 'de la page'}</label>
            <input
              id={fieldId}
              ref={inputRef}
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder={asTemplate ? 'Gabarit de page…' : 'Nouvelle page…'}
              className="input w-full"
            />
          </div>

          {/* Page vierge sur un site qui a un modèle par défaut : reprendre ses
              blocs est le comportement attendu d'un « modèle PAR DÉFAUT », pas
              un extra à redemander à chaque page. La case reste visible et
              décochable — jamais un automatisme muet. */}
          {!asTemplate && defaultModel && (
            <label htmlFor={defaultModelId} className="flex items-start gap-2.5 cursor-pointer">
              <input
                id={defaultModelId}
                type="checkbox"
                checked={useDefaultModel}
                onChange={e => setUseDefaultModel(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-iris-600 focus:ring-2 focus:ring-iris-500"
              />
              <span className="text-sm text-slate-600">
                Reprendre les blocs du gabarit par défaut du site,{' '}
                <strong>{defaultModel.title || defaultModel.slug}</strong>.
              </span>
            </label>
          )}

          {/* Démarrer le modèle depuis une page EXISTANTE du site : le chemin
              naturel vers le premier modèle, dont l'habillage est déjà posé sur
              une page réelle. Recherche à autocomplétion plutôt qu'un <select>
              natif listant TOUTES les pages : un site peut en porter plusieurs
              centaines, et le <select> les téléchargeait toutes pour n'en
              choisir qu'une. */}
          {asTemplate && !directoryId && (
            <div>
              <label htmlFor={sourceId} className="field-label">Partir de</label>
              <BuilderPageAutocomplete
                id={sourceId}
                siteId={siteId}
                value={sourceSlug}
                onChange={setSourceSlug}
                describedById={`${sourceId}-hint`}
              />
              <p id={`${sourceId}-hint`} className="mt-1.5 text-xs text-slate-500">
                Les blocs de la page choisie sont COPIÉS dans le gabarit : la page d’origine n’est pas touchée.
              </p>
            </div>
          )}

          {asTemplate && (
            <p className="text-xs text-slate-500">
              Un gabarit n&apos;est jamais servi publiquement : il sert de point de
              départ à la création de pages. Pour qu&apos;un header ou un footer
              reste <strong>commun</strong> à toutes les pages qui en descendront,
              remplacez-le dans le gabarit par un « bloc existant » — une copie,
              elle, vivra sa vie sur chaque page.
            </p>
          )}

          {error && <p role="alert" className="text-xs text-rose-600">{error}</p>}
        </div>

        <div className="modal-footer">
          <button type="button" onClick={onClose} className="btn btn-secondary">Annuler</button>
          <button type="submit" className="btn btn-primary" disabled={!title.trim() || creating}>
            {creating
              ? 'Création…'
              : asTemplate
                ? (sourceSlug ? 'Créer le gabarit depuis cette page' : 'Créer le gabarit')
                : 'Créer la page'}
          </button>
        </div>
      </form>
    </div>
  )
}
